import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SiteShell } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { Prose } from "@/components/site/Prose";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { buildHead } from "@/lib/seo-head";
import { BLOG_CATEGORIES, BLOG_POSTS, formatBlogDate, getPost } from "@/data/blog-posts";
import type { BlogPost as BlogPostType } from "@/data/blog-posts";
import { Button } from "@/components/ui/button";
import {
  ArticleHeader,
  ArticleTakeaways,
  ArticleOutline,
  headingComponents,
} from "@/components/blog/ArticleFurniture";
import { ArticleEnding } from "@/components/blog/ArticleEnding";
import { QuickDscrCheck } from "@/components/blog/QuickDscrCheck";
import { QuickQuoteWidget } from "@/components/site/QuickQuoteWidget";
import { AskSamCard } from "@/components/blog/AskSamCard";
import { BlogPostImage, blogFallbackImage } from "@/components/blog/BlogPostImage";
import {
  extractOutline,
  extractQuickAnswer,
  extractTakeaways,
  cleanArticleMarkdown,
  extractExpertInsight,
  splitArticleForBanners,
  blogProductKey,
  extractFaqs,
  blogPostingJsonLd,
} from "@/lib/blog/article-outline";
import {
  getPublishedPost,
  getRelatedPosts,
  getPublishedClusterPosts,
  type PublicBlogPost,
} from "@/lib/blog-public.functions";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    // Published articles created by the blog engine win; hand-authored
    // articles that ship with the site remain the fallback.
    let dbPost: PublicBlogPost | null = null;
    let related: PublicBlogPost[] = [];
    let clusterPosts: PublicBlogPost[] = [];
    try {
      dbPost = (await getPublishedPost({ data: { slug: params.slug } })) as PublicBlogPost | null;
      if (dbPost) {
        if (dbPost.cluster_id && ["pillar", "supporting"].includes(dbPost.article_role ?? "")) {
          try {
            clusterPosts = (await getPublishedClusterPosts({ data: { clusterId: dbPost.cluster_id, slug: params.slug, limit: 6 } })) as PublicBlogPost[];
          } catch {
            clusterPosts = [];
          }
        }
        try {
          related = (await getRelatedPosts({
            data: {
              slug: params.slug,
              category: dbPost.category,
              tags: dbPost.tags,
              clusterId: dbPost.cluster_id,
              excludeSlugs: clusterPosts.map((post) => post.slug),
              limit: 3,
            },
          })) as PublicBlogPost[];
        } catch {
          related = [];
        }
      }
    } catch {
      dbPost = null;
    }
    if (dbPost) return { dbPost, related, clusterPosts, post: null };
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { dbPost: null, related: [] as PublicBlogPost[], clusterPosts: [] as PublicBlogPost[], post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const { dbPost } = loaderData;
    if (dbPost) {
      const body = dbPost.body_md ?? "";
      const description = dbPost.meta_description ?? dbPost.excerpt ?? "";
      const image = dbPost.hero_image_url ?? dbPost.cover_url ?? blogFallbackImage(dbPost.category);
      return buildHead({
        title: dbPost.meta_title ?? dbPost.title,
        description,
        url: `/blog/${dbPost.slug}`,
        keywords: (dbPost.tags ?? []).join(", "),
        image,
        imageAlt: dbPost.cover_alt ?? `Investment property financing guide: ${dbPost.title}`,
        type: "article",
        publishedTime: dbPost.published_at ?? dbPost.updated_at,
        modifiedTime: dbPost.content_updated_at ?? dbPost.published_at ?? undefined,
        breadcrumbs: [
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: dbPost.title, url: `/blog/${dbPost.slug}` },
        ],
        faqs: extractFaqs(body),
        speakableSelectors: ["#answer-summary", "[data-answer-first] li"],
        service: { name: "Investor property financing" },
        extraJsonLd: [
          blogPostingJsonLd({
            url: `/blog/${dbPost.slug}`,
            headline: dbPost.title,
            description,
            image: typeof image === "string" ? image : null,
            datePublished: dbPost.published_at ?? dbPost.updated_at,
            dateModified: dbPost.content_updated_at ?? dbPost.published_at,
            keywords: dbPost.tags ?? [],
            wordCount: body.split(/\s+/).filter(Boolean).length,
          }),
        ],
      });
    }
    const post = loaderData.post!;
    return buildHead({
      title: post.metaTitle,
      description: post.metaDescription,
      url: `/blog/${post.slug}`,
      keywords: post.keywords,
      type: "article",
      publishedTime: post.publishedAt,
      modifiedTime: post.updatedAt,
      faqs: post.faqs,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${post.slug}` },
      ],
      service: { name: "Investor property financing" },
    });
  },
  notFoundComponent: PostNotFound,
  component: BlogRoute,
});

function BlogRoute() {
  const data = Route.useLoaderData() as {
    dbPost: PublicBlogPost | null;
    related: PublicBlogPost[];
    clusterPosts: PublicBlogPost[];
    post: BlogPostType | null;
  };
  if (data.dbPost) return <PublishedArticle post={data.dbPost} related={data.related} clusterPosts={data.clusterPosts} />;
  return <BlogPost />;
}

function PublishedArticle({
  post,
  related,
  clusterPosts,
}: {
  post: PublicBlogPost;
  related: PublicBlogPost[];
  clusterPosts: PublicBlogPost[];
}) {
  const rawMd = post.body_md ?? "";
  const expertInsight = extractExpertInsight(rawMd);
  const md = cleanArticleMarkdown(rawMd, post.title);
  const outline = extractOutline(md);
  const answer = extractQuickAnswer(rawMd) || post.excerpt || "";
  const takeaways = extractTakeaways(rawMd);
  const cat = BLOG_CATEGORIES.find((c) => c.slug === post.category);
  const path = `/blog/${post.slug}`;
  const chunks = splitArticleForBanners(md);
  const markdownComponents = headingComponents();
  const productKey = blogProductKey(post.category, post.tags ?? []);
  const cover = post.hero_image_url ?? post.cover_url;

  return (
    <SiteShell>
      <ArticleHeader
        category={cat?.name ?? post.category ?? "Investor guides"}
        title={post.title}
        excerpt={post.excerpt ?? undefined}
        author={post.author_name ?? "Simply Approved Mortgages"}
        published={formatBlogDate(post.published_at ?? post.updated_at)}
        publishedDateTime={post.published_at ?? post.updated_at}
        updated={
          post.content_updated_at && post.published_at && post.content_updated_at.slice(0, 10) > post.published_at.slice(0, 10)
            ? formatBlogDate(post.content_updated_at)
            : null
        }
        updatedDateTime={post.content_updated_at ?? null}
        readMins={post.read_mins}
        path={path}
      />
      <Section>
        <article className="mx-auto max-w-4xl">
          <BlogPostImage
            src={cover}
            alt={post.cover_alt}
            title={post.title}
            category={post.category}
            eager
            className="mb-10 aspect-[1200/630] w-full rounded-lg border border-border object-cover shadow-lg"
          />
          <ArticleTakeaways answer={answer} points={takeaways} />
          <div className="mt-8">
            <ArticleOutline items={outline} />
          </div>
          <Prose>
            <div className="mt-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {chunks[0]}
              </ReactMarkdown>
            </div>
          </Prose>

          {/* Mid-article capture: reached by most readers, no page change. */}
          <QuickQuoteWidget sourcePage="blog-mid" compact />

          {chunks[1] ? (
            <Prose>
              <div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {chunks[1]}
                </ReactMarkdown>
              </div>
            </Prose>
          ) : null}

          <AskSamCard productKey={productKey} topic={cat?.name ?? "this topic"} />

          {chunks[2] ? (
            <Prose>
              <div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {chunks[2]}
                </ReactMarkdown>
              </div>
            </Prose>
          ) : null}

          <QuickDscrCheck />
          <ProductReportCtaBanner productKey={productKey} variant="article" />

          <ArticleEnding
            path={path}
            title={post.title}
            updatedAt={post.updated_at}
            expertInsight={expertInsight}
            clusterPosts={clusterPosts}
            relatedPosts={related}
          />
        </article>
      </Section>
    </SiteShell>
  );
}

function PostNotFound() {
  return (
    <SiteShell>
      <Section>
        <h1 className="text-2xl font-black">Article not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          That article has moved or is no longer published.
        </p>
        <Button asChild className="mt-6">
          <Link to="/blog">Back to the blog</Link>
        </Button>
      </Section>
    </SiteShell>
  );
}

function BlogPost() {
  const { post } = Route.useLoaderData() as { post: BlogPostType | null };
  if (!post) return null;
  const cat = BLOG_CATEGORIES.find((c) => c.slug === post.category);
  const related = BLOG_POSTS.filter((candidate) => candidate.slug !== post.slug)
    .sort((a, b) => Number(b.category === post.category) - Number(a.category === post.category))
    .slice(0, 3)
    .map((candidate) => ({
      slug: candidate.slug,
      title: candidate.title,
      excerpt: candidate.excerpt,
      category: candidate.category,
      article_role: null,
      hero_image_url: null,
      cover_url: null,
      cover_alt: null,
    }));

  return (
    <SiteShell>
      <ArticleHeader
        category={cat?.name ?? "Investor guides"}
        title={post.title}
        excerpt={post.excerpt}
        author={post.author}
        published={formatBlogDate(post.publishedAt)}
        publishedDateTime={post.publishedAt}
        updated={formatBlogDate(post.updatedAt)}
        updatedDateTime={post.updatedAt}
        readMins={post.readMinutes}
        path={`/blog/${post.slug}`}
      />
      <Section>
        <article className="mx-auto max-w-3xl">
          <BlogPostImage
            title={post.title}
            category={post.category}
            eager
            className="mb-10 aspect-[1200/630] w-full rounded-lg border border-border object-cover shadow-lg"
          />

          {post.sections.map((s, i) => (
            <div key={s.heading}>
              <section className="mt-10">
                <h2 className="text-xl font-bold tracking-tight">{s.heading}</h2>
                {s.body.map((b) => (
                  <p key={b} className="mt-3 leading-relaxed text-muted-foreground">
                    {b}
                  </p>
                ))}
                {s.bullets?.length ? (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2 text-sm text-muted-foreground">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                        {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
              {/* Mid-article CTA: long reads lose intent before the footer CTA,
                  so surface one after the reader has consumed real substance.
                  Only on posts long enough that it isn't stacked on the closer. */}
              {i === 1 && post.sections.length >= 4 ? (
                <aside className="mt-10 rounded-2xl border border-brand/25 bg-brand/5 p-5">
                  <div className="text-sm font-bold">Want this priced on your actual deal?</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Run live DSCR scenarios yourself, or send the file to a licensed loan officer
                    for a scenario review. Figures are illustrative and subject to full
                    underwriting.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button asChild size="sm">
                      <Link to="/rates">Check today's DSCR rates</Link>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/apply">Request a scenario review</Link>
                    </Button>
                  </div>
                </aside>
              ) : null}
            </div>
          ))}


          {post.faqs.length ? (
            <section className="mt-12">
              <h2 className="text-xl font-bold tracking-tight">Frequently asked questions</h2>
              <dl className="mt-4 space-y-5">
                {post.faqs.map((f) => (
                  <div key={f.q}>
                    <dt className="font-semibold">{f.q}</dt>
                    <dd className="mt-1 text-sm text-muted-foreground">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          <ArticleEnding path={`/blog/${post.slug}`} title={post.title} updatedAt={post.updatedAt} relatedPosts={related} />
        </article>
      </Section>
    </SiteShell>
  );
}
